import { useEffect, useRef } from 'react'

/**
 * ReplaiWidget – React-Wrapper für das Replai-Chat-Widget.
 *
 * Diese Komponente lädt den offiziellen Widget-Loader (`/widget.js`) und mountet
 * ihn in die Seite. Dadurch erhältst du automatisch den vollen Funktionsumfang
 * (Chat, Lead-Erfassung, Service-Area-Karte, Rate-Limiting) – ohne eigene Logik.
 *
 * Der Loader rendert das Widget standardmäßig fixiert in einer Bildschirmecke
 * (Position kommt aus deinen Replai-Einstellungen). Im `inline`-Modus wird es
 * stattdessen relativ an der Stelle gerendert, an der die Komponente steht.
 *
 * @param {object}  props
 * @param {string}  props.customerId        Deine Customer-ID aus dem Replai-Dashboard (Pflicht).
 * @param {string} [props.host]             Basis-URL, von der das Widget geladen wird. Default: 'https://replai.studio'.
 * @param {string} [props.apiUrl]           Optionale abweichende API-URL (sonst wird die Host-Origin verwendet).
 * @param {boolean}[props.inline=false]     Wenn true, wird das Widget inline (relativ) statt fixiert gerendert.
 * @param {string} [props.className]        CSS-Klasse für den Inline-Container (nur im inline-Modus relevant).
 * @param {object} [props.style]            Inline-Styles für den Inline-Container (nur im inline-Modus relevant).
 */
export default function ReplaiWidget({
  customerId,
  host = 'https://replai.studio',
  apiUrl,
  inline = false,
  className,
  style,
}) {
  const containerRef = useRef(null)

  useEffect(() => {
    // SSR-Guard: ohne DOM (z. B. Next.js Server-Rendering) nichts tun.
    if (typeof document === 'undefined') return

    if (!customerId) {
      console.warn('[ReplaiWidget] Die Prop "customerId" ist erforderlich.')
      return
    }

    const normalizedHost = host.replace(/\/+$/, '')
    const mountTarget = inline ? containerRef.current : document.body
    if (!mountTarget) return

    // Doppel-Injektion verhindern (z. B. React StrictMode oder mehrere Instanzen
    // mit derselben customerId). Der Loader würde sonst zwei Widgets erzeugen.
    const selector = `script[data-replai-loader="${customerId}"]`
    if (mountTarget.querySelector(selector) || document.getElementById('replai-cw-host')) {
      return
    }

    const script = document.createElement('script')
    script.src = `${normalizedHost}/widget.js`
    script.async = true
    script.setAttribute('data-customer-id', customerId)
    script.setAttribute('data-replai-loader', customerId)
    if (apiUrl) script.setAttribute('data-api-url', apiUrl)
    if (inline) script.setAttribute('data-inline', 'true')

    mountTarget.appendChild(script)

    return () => {
      script.remove()
      // Der Loader hängt sein Wurzel-Element mit der id "replai-cw-host" an.
      // Bei fixierter Darstellung liegt es auf <body> und muss explizit entfernt
      // werden. Im inline-Modus liegt es im Container und wird mit diesem entfernt.
      const root = (inline ? containerRef.current : document)?.querySelector?.('#replai-cw-host')
      root?.remove()
    }
  }, [customerId, host, apiUrl, inline])

  // Im inline-Modus brauchen wir einen Anker, in den der Loader rendert.
  // Im fixierten Modus rendert das Widget auf <body> – wir geben nichts aus.
  return inline ? <div ref={containerRef} className={className} style={style} /> : null
}
